/**
 * Deterministic SITREP / CAD-note generators — pure templates over the
 * structured ITASC. No AI: this is the always-available baseline that works
 * with `AI_ENABLED=false`. When AI is on, the LLM may only *rephrase* this
 * output under the grounding contract (it must not add facts).
 *
 * Copy stays advisory and factual; it never issues commands.
 */

import type { Incident } from './incident.js';
import {
  CONFIRMATION_LABELS,
  SHOWING_LABELS,
  STRATEGY_LABELS,
  FIRE_STATUS_LABELS,
  labelForTag,
} from './incident.js';

const join = (parts: (string | undefined | false)[], sep = ' '): string =>
  parts.filter((p): p is string => Boolean(p && p.trim())).join(sep);

const sentence = (s: string): string => {
  const t = s.trim().replace(/\s+/g, ' ');
  if (!t) return '';
  const cap = t[0]!.toUpperCase() + t.slice(1);
  return /[.!?]$/.test(cap) ? cap : `${cap}.`;
};

const tagList = (tags: readonly string[]): string => tags.map(labelForTag).join(', ');

/** ITASC, radio-ready: five labelled lines, omitting empty sections. */
export function buildRadioSitrep(incident: Incident): string {
  const { itasc } = incident;
  const lines: string[] = [];

  // I — Incident
  const inc = itasc.incident;
  const iParts = join([
    CONFIRMATION_LABELS[inc.confirmation],
    inc.incidentType,
    inc.buildingType && `${inc.buildingType}`,
    inc.showing && SHOWING_LABELS[inc.showing],
    inc.size,
    inc.intensity && `${inc.intensity} intensity`,
    inc.fireStatus && FIRE_STATUS_LABELS[inc.fireStatus],
  ], ', ');
  const iLoc = `Location ${incident.location}${inc.locationConfirmed ? ' confirmed' : ' (to confirm)'}`;
  lines.push(`I — Incident: ${sentence(join([iParts, iLoc], '. '))}`);

  // T — Threats
  const th = itasc.threats;
  const tParts = join([
    th.life && `Life: ${th.life}`,
    th.selected.length > 0 && tagList(th.selected),
    th.notes,
  ], '. ');
  if (tParts) lines.push(`T — Threats: ${sentence(tParts)}`);

  // A — Actions
  const ac = itasc.actions;
  const aParts = join([
    ac.strategy && STRATEGY_LABELS[ac.strategy],
    ac.selected.length > 0 && tagList(ac.selected),
    ac.notes,
  ], '; ');
  if (aParts) lines.push(`A — Actions: ${sentence(aParts)}`);

  // S — Support
  const su = itasc.support;
  const sParts = join([
    su.requested.length > 0 && `Requesting ${tagList(su.requested)}`,
    su.unresolvedGaps.length > 0 && `Outstanding: ${tagList(su.unresolvedGaps)}`,
    su.notes,
  ], '. ');
  if (sParts) lines.push(`S — Support: ${sentence(sParts)}`);

  // C — Command and Control. Only emitted when there is real command content
  // (an explicit incident name, control taken, channel or staging) — the
  // incident header already carries the name, so we don't pad an empty line.
  const cmd = itasc.command;
  const cParts = join([
    cmd.incidentName,
    cmd.controlTaken && cmd.incidentController && `${cmd.incidentController} in control`,
    cmd.radioChannel && `Channel ${cmd.radioChannel}`,
    cmd.staging && `Staging ${cmd.staging}`,
  ], '. ');
  if (cParts) lines.push(`C — Command: ${sentence(cParts)}`);

  return lines.join('\n');
}

/** Compact single-block note suitable for pasting into a CAD/IMS log. */
export function buildCadNote(incident: Incident): string {
  const { itasc } = incident;
  const segs: string[] = [];

  segs.push(CONFIRMATION_LABELS[itasc.incident.confirmation]);
  if (itasc.incident.showing) segs.push(SHOWING_LABELS[itasc.incident.showing]);
  if (itasc.threats.life) segs.push(`life: ${itasc.threats.life}`);
  if (itasc.threats.selected.length) segs.push(`threats: ${tagList(itasc.threats.selected)}`);
  if (itasc.actions.strategy) segs.push(`strategy: ${STRATEGY_LABELS[itasc.actions.strategy]}`);
  if (itasc.support.requested.length) segs.push(`req: ${tagList(itasc.support.requested)}`);
  if (itasc.support.unresolvedGaps.length) segs.push(`gaps: ${tagList(itasc.support.unresolvedGaps)}`);

  const head = `[${incident.name}] ${incident.location}`;
  return `${head} — ${segs.join('; ')}.`;
}
